import { z } from "zod";
import { nullishToOptional } from "../../../helpers";
import { areShallowObjectsEqual, mapByUnique } from "../../../utils";
import { ColorTokenInlineData } from "../../properties";
import { Size } from "../primitives";
import { DesignTokenType } from "../raw-element";
import { DimensionTokenData } from "./dimension";

//
// Enums
//

export const PageBlockCalloutType = z.enum(["Info", "Primary", "Success", "Warning", "Error"]);

export const PageBlockTypeV1 = z.enum([
  "Text",
  "Heading",
  "Code",
  "UnorderedList",
  "OrderedList",
  "Quote",
  "Callout",
  "Divider",
  "Image",
  "Embed",
  "Link",
  "Shortcuts",
  "FigmaEmbed",
  "YoutubeEmbed",
  "StorybookEmbed",
  "Token",
  "TokenGroup",
  "TokenList",
  "Component",
  "ComponentGroup",
  "Theme",
  "ComponentSandbox",
  "Custom",
  "FigmaFrames",
  "ComponentAssets",
  "ComponentAssetGroup",
  "RenderCode",
  "Tabs",
  "TabItem",
  "Table",
  "TableRow",
  "TableCell",
  "Guidelines",
  "Guideline",
  "FigmaComponents",
  "FigmaComponentPropsTable",
  "Files",
]);

export const PageBlockCodeLanguage = z.enum([
  "Angular",
  "Bash",
  "C",
  "Cpp",
  "Csharp",
  "CSS",
  "Dart",
  "Handlebars",
  "HTML",
  "Java",
  "Javascript",
  "JSON",
  "ReactJSX",
  "ReactTSX",
  "Kotlin",
  "Lua",
  "Markdown",
  "ObjectiveC",
  "PHP",
  "Plain",
  "Python",
  "Ruby",
  "Rust",
  "Sass",
  "SCSS",
  "Svetle",
  "Swift",
  "Twig",
  "Typescript",
  "Vue",
  "XML",
  "YAML",
]);

export const PageBlockAlignment = z.enum(["Left", "Center", "Stretch", "Right"]);
export const PageBlockThemeType = z.enum(["Override", "Comparison"]);
export const PageBlockTokenNameFormat = z.enum(["Name", "GroupAndName", "FullPath", "CustomProperty"]);
export const PageBlockAssetType = z.enum(["image", "figmaFrame"]);

export const PageBlockTilesAlignment = z.enum(["Center", "FrameHeight"]);

export const PageBlockTilesLayout = z.enum(["C8", "C7", "C6", "C5", "C4", "C3", "C2", "C1", "C1_75"]);

//
// Support types
//

export const PageBlockTheme = z.object({
  themeIds: z.array(z.string()),
  type: PageBlockThemeType,
});

export const PageBlockSwatch = z.object({
  id: z.string(),
  selectedThemeIds: z.string().array(),
});

export const PageBlockUrlPreview = z.object({
  title: nullishToOptional(z.string()),
  description: nullishToOptional(z.string()),
  thumbnailUrl: nullishToOptional(z.string()),
});

export const PageBlockFrameOrigin = z.object({
  sourceFileName: nullishToOptional(z.string()),
  title: nullishToOptional(z.string()),
  previewUrl: nullishToOptional(z.string()),
  valid: nullishToOptional(z.boolean()),
  referenceId: nullishToOptional(z.string()),
  assetId: nullishToOptional(z.string()),
  assetScale: nullishToOptional(z.number()),
  width: nullishToOptional(z.number()),
  height: nullishToOptional(z.number()),
});

export const PageBlockFrame = z.object({
  persistentId: z.string(),
  sourceId: z.string(),
  sourceFrameId: z.string(),
  title: nullishToOptional(z.string()),
  description: nullishToOptional(z.string()),
  backgroundColor: nullishToOptional(ColorTokenInlineData),
  origin: nullishToOptional(PageBlockFrameOrigin),
});

export const PageBlockAsset = z.object({
  type: PageBlockAssetType,
  id: nullishToOptional(z.string()),
  url: nullishToOptional(z.string()),
  figmaFrame: nullishToOptional(PageBlockFrame),
});

export const PageBlockLinkPreview = z.object({
  title: nullishToOptional(z.string()),
  valid: nullishToOptional(z.boolean()),
});

export const PageBlockShortcut = z.object({
  persistentId: z.string(),
  title: nullishToOptional(z.string()),
  description: nullishToOptional(z.string()),
  asset: nullishToOptional(PageBlockAsset),
  documentationItemId: nullishToOptional(z.string()),
  pageHeadingId: nullishToOptional(z.string()),
  url: nullishToOptional(z.string()),
  openInNewTab: nullishToOptional(z.boolean()),
  urlPreview: nullishToOptional(PageBlockUrlPreview),
  documentationItemPreview: nullishToOptional(PageBlockLinkPreview),
});

export const PageBlockCustomBlockPropertyImageValue = z.object({
  asset: nullishToOptional(PageBlockAsset),
  assetId: nullishToOptional(z.string()),
  assetUrl: nullishToOptional(z.string()),
});

export const PageBlockCustomBlockPropertyValue = z.object({
  key: z.string(),
  value: z.any(),
});

export const PageBlockFigmaFrameProperties = z.object({
  color: nullishToOptional(
    z.object({
      value: z.string(),
    })
  ),
  alignment: PageBlockTilesAlignment,
  previewContainerHeight: nullishToOptional(z.number()),
  layout: PageBlockTilesLayout,
  backgroundColor: nullishToOptional(ColorTokenInlineData),
  showTitles: z.boolean(),
});

export const PageBlockRenderCodeProperties = z.object({
  showCode: z.boolean(),
  showControls: z.boolean().optional(),
});

export const PageBlockAssetComponent = z.object({
  persistentId: z.string(),
  componentAssetId: z.string(),
  title: nullishToOptional(z.string()),
  description: nullishToOptional(z.string()),
  backgroundColor: nullishToOptional(ColorTokenInlineData),
});

export const PageBlockTableColumn = z.object({
  id: z.string(),
  width: DimensionTokenData,
});

export const PageBlockTableProperties = z.object({
  showBorders: z.boolean(),
  showHeaderRow: z.boolean(),
  showHeaderColumn: z.boolean(),
  columns: z.array(PageBlockTableColumn),
});

export const PageBlockFigmaComponentBlockConfig = z.object({
  showComponentName: nullishToOptional(z.boolean()),
  showComponentDescription: nullishToOptional(z.boolean()),
  showPropertyList: nullishToOptional(z.boolean()),
  previewOrderIds: nullishToOptional(z.array(z.string())),
  previewContainerSize: nullishToOptional(z.enum(["Centered", "NaturalHeight"])),
  previewContainerHeight: nullishToOptional(z.number()),
  backgroundColor: nullishToOptional(ColorTokenInlineData),
});

export const PageBlockTokenBlockConfig = z.object({
  tokenNameFormat: nullishToOptional(PageBlockTokenNameFormat),
  tokenNameCustomPropertyId: nullishToOptional(z.string()),
});

export const PageBlockAssetBlockConfig = z.object({
  showSearch: nullishToOptional(z.boolean()),
  showAssetDescription: nullishToOptional(z.boolean()),
});

export const PageBlockSelectedFigmaComponent = z.object({
  figmaComponentId: z.string(),
  selectedComponentProperties: z.string().array(),
  selectedComponentVariants: z.record(z.string().array()).optional(),
});

export const PageBlockStorybookItem = z.object({
  entityId: z.string().optional(),
  embed: z
    .object({
      inputUrl: z.string(),
      embedUrl: z.string(),
    })
    .optional(),
  entityMeta: z
    .object({
      caption: z.string().optional(),
      height: z.number().optional(),
      hiddenProps: z.array(z.string()).optional(),
      propValues: z.record(z.unknown()).optional(),
    })
    .optional(),
});

export const PageBlockStorybookBlockConfig = z.object({
  value: PageBlockStorybookItem.array(),

  showCode: z.boolean().optional(),
  showFooter: z.boolean().optional(),
  showProperties: z.boolean().optional(),
  showDescription: z.boolean().optional(),
  showDefaults: z.boolean().optional(),
});

//
// Rich text
//

export const PageBlockTextSpanAttributeType = z.enum(["Bold", "Italic", "Link", "Strikethrough", "Code", "Comment"]);

export const PageBlockTextSpanAttribute = z.object({
  type: PageBlockTextSpanAttributeType,

  // Link attributes
  link: nullishToOptional(z.string()),
  documentationItemId: nullishToOptional(z.string()),

  /** @deprecated use openInNewTab */
  openInNewWindow: nullishToOptional(z.boolean()),
  openInNewTab: nullishToOptional(z.boolean()),

  // Comment attributes
  commentHighlightId: nullishToOptional(z.string()),
  commentIsResolved: nullishToOptional(z.boolean()),
});

export const PageBlockTextSpan = z.object({
  text: z.string(),
  attributes: z.array(PageBlockTextSpanAttribute),
});

export const PageBlockText = z.object({
  spans: z.array(PageBlockTextSpan),
});

export const PageBlockGuideline = z.object({
  description: nullishToOptional(z.string()),
  asset: nullishToOptional(PageBlockAsset),
  type: z.string(),
  imageAlt: nullishToOptional(z.string()),
  imageCaption: nullishToOptional(z.string()),
  imageAlignment: nullishToOptional(PageBlockAlignment),
  openLightbox: nullishToOptional(z.boolean()),
  isBordered: nullishToOptional(z.boolean()),
});

export const PageBlockFile = z.object({
  entityId: z.string(),
  entityMeta: z
    .object({
      title: z.string().optional(),
      description: z.string().optional(),
    })
    .optional(),
});

export type PageBlockCalloutType = z.infer<typeof PageBlockCalloutType>;
export type PageBlockCodeLanguage = z.infer<typeof PageBlockCodeLanguage>;
export type PageBlockAlignment = z.infer<typeof PageBlockAlignment>;
export type PageBlockThemeType = z.infer<typeof PageBlockThemeType>;
export type PageBlockAssetType = z.infer<typeof PageBlockAssetType>;
export type PageBlockTilesAlignment = z.infer<typeof PageBlockTilesAlignment>;
export type PageBlockTilesLayout = z.infer<typeof PageBlockTilesLayout>;
export type PageBlockTextSpanAttributeType = z.infer<typeof PageBlockTextSpanAttributeType>;

export type PageBlockTypeV1 = z.infer<typeof PageBlockTypeV1>;
export type PageBlockTextSpanAttribute = z.infer<typeof PageBlockTextSpanAttribute>;
export type PageBlockTextSpan = z.infer<typeof PageBlockTextSpan>;
export type PageBlockText = z.infer<typeof PageBlockText>;
export type PageBlockGuideline = z.infer<typeof PageBlockGuideline>;
export type PageBlockFrame = z.infer<typeof PageBlockFrame>;
export type PageBlockFrameOrigin = z.infer<typeof PageBlockFrameOrigin>;
export type PageBlockAsset = z.infer<typeof PageBlockAsset>;
export type PageBlockTableColumn = z.infer<typeof PageBlockTableColumn>;
export type PageBlockTheme = z.infer<typeof PageBlockTheme>;
export type PageBlockSwatch = z.infer<typeof PageBlockSwatch>;
export type PageBlockShortcut = z.infer<typeof PageBlockShortcut>;
export type PageBlockCustomBlockPropertyValue = z.infer<typeof PageBlockCustomBlockPropertyValue>;
export type PageBlockCustomBlockPropertyImageValue = z.infer<typeof PageBlockCustomBlockPropertyImageValue>;
export type PageBlockFigmaComponentBlockConfig = z.infer<typeof PageBlockFigmaComponentBlockConfig>;
export type PageBlockTokenNameFormat = z.infer<typeof PageBlockTokenNameFormat>;

//
// Block
//

export const PageBlockBaseV1 = z.object({
  persistentId: z.string(),
  type: PageBlockTypeV1,

  // V2 generic attributes
  numberOfColumns: nullishToOptional(z.number()),
  itemBackgroundColor: nullishToOptional(ColorTokenInlineData),

  // Element linking
  designObjectId: nullishToOptional(z.string()),
  designObjectIds: nullishToOptional(z.array(z.string())),
  tokenType: nullishToOptional(DesignTokenType.or(z.literal("Font"))),
  showNestedGroups: nullishToOptional(z.boolean()),
  brandId: nullishToOptional(z.string()),

  // Rich text
  text: nullishToOptional(PageBlockText),
  caption: nullishToOptional(z.string()),
  headingType: nullishToOptional(z.number().min(1).max(3)),
  codeLanguage: nullishToOptional(PageBlockCodeLanguage),
  calloutType: nullishToOptional(PageBlockCalloutType),
  urlInput: nullishToOptional(z.string()),
  url: nullishToOptional(z.string()),
  urlPreview: nullishToOptional(PageBlockUrlPreview),

  // Image
  asset: nullishToOptional(PageBlockAsset),
  alignment: nullishToOptional(PageBlockAlignment),
  imageAlt: nullishToOptional(z.string()),
  openLightbox: nullishToOptional(z.boolean()),
  isBordered: nullishToOptional(z.boolean()),

  // Shortcuts block
  shortcuts: nullishToOptional(z.array(PageBlockShortcut)),

  // Guidelines
  guideline: nullishToOptional(PageBlockGuideline),

  // Custom blocks
  customBlockKey: nullishToOptional(z.string()),
  customBlockProperties: nullishToOptional(z.array(PageBlockCustomBlockPropertyValue)),
  variantKey: nullishToOptional(z.string()),

  // Figma frames
  figmaFrameProperties: nullishToOptional(PageBlockFigmaFrameProperties),
  figmaFrames: nullishToOptional(z.array(PageBlockFrame)),

  // Generic
  size: nullishToOptional(Size),
  backgroundColor: nullishToOptional(ColorTokenInlineData),

  // Render code
  renderCodeProperties: nullishToOptional(PageBlockRenderCodeProperties),

  // Component assets
  componentAssets: nullishToOptional(z.array(PageBlockAssetComponent)),

  // Tables
  tableProperties: nullishToOptional(PageBlockTableProperties),
  columnId: nullishToOptional(z.string()),

  // Design tokens
  theme: nullishToOptional(PageBlockTheme),
  swatches: nullishToOptional(PageBlockSwatch.array()),
  blacklistedElementProperties: nullishToOptional(z.array(z.string())),
  tokenBlockConfig: nullishToOptional(PageBlockTokenBlockConfig),

  // (Vector) assets
  assetBlockConfig: nullishToOptional(PageBlockAssetBlockConfig),

  // Figma components
  figmaComponentsBlockConfig: nullishToOptional(PageBlockFigmaComponentBlockConfig),
  selectedFigmaComponent: nullishToOptional(PageBlockSelectedFigmaComponent),
  selectedFigmaComponents: nullishToOptional(PageBlockSelectedFigmaComponent.array()),

  // Arbitrary
  userMetadata: nullishToOptional(z.string()),

  // Storybook
  storybookBlockConfig: nullishToOptional(PageBlockStorybookBlockConfig),

  // Files
  files: nullishToOptional(PageBlockFile.array()),
});

type PageBlockV1Input = z.input<typeof PageBlockBaseV1> & {
  children: PageBlockV1Input[] | null | undefined;
};

export const PageBlockV1: z.ZodType<PageBlockV1, z.ZodTypeDef, PageBlockV1Input> = PageBlockBaseV1.extend({
  children: z.lazy(
    () =>
      PageBlockV1.array()
        .nullish()
        .transform(t => t ?? []) as z.ZodType<PageBlockV1[], z.ZodTypeDef, PageBlockV1Input[]>
  ),
});

export type PageBlockV1 = z.infer<typeof PageBlockBaseV1> & {
  children: PageBlockV1[];
};

//
// Utility methods
//

export function traversePageBlocksV1(blocks: PageBlockV1[], action: (block: PageBlockV1) => void) {
  for (const block of blocks) {
    action(block);
    traversePageBlocksV1(block.children, action);
  }
}

export function removeCommentSpans(text: PageBlockText): PageBlockText {
  const { spans, ...rest } = text;

  const newSpans = spans.map(s => {
    return {
      ...s,
      attributes: s.attributes.filter(a => a.type !== "Comment"),
    };
  });

  const updatedRichText = {
    ...rest,
    spans: newSpans,
  };

  return joinRepeatingSpans(updatedRichText);
}

export function joinRepeatingSpans(text: PageBlockText): PageBlockText {
  if (text.spans.length < 2) return text;

  // Make sure spans are clean before comparing them
  text = sanitizeSpans(text);
  const { spans, ...rest } = text;

  let previousSpan = { ...spans[0] };
  const newSpans = [previousSpan];

  for (let i = 1; i < spans.length; i++) {
    const span = spans[i];
    if (areAttributesEqual(span.attributes, previousSpan.attributes)) {
      previousSpan.text += span.text;
    } else {
      previousSpan = { ...span };
      newSpans.push(previousSpan);
    }
  }

  return {
    ...rest,
    spans: newSpans,
  };
}

function areAttributesEqual(lhs: PageBlockTextSpanAttribute[], rhs: PageBlockTextSpanAttribute[]) {
  if (lhs.length !== rhs.length) return false;

  const lhsMap = mapByUnique(lhs, i => i.type);
  for (const rhsAttribute of rhs) {
    const lhsAttribute = lhsMap.get(rhsAttribute.type);
    if (!areShallowObjectsEqual(lhsAttribute, rhsAttribute)) return false;
  }

  return true;
}

function sanitizeSpans(text: PageBlockText): PageBlockText {
  const { spans, ...rest } = text;

  return {
    ...rest,
    spans: spans.map(s => {
      const { attributes, ...rest } = s;

      return {
        ...rest,
        attributes: attributes.map(sanitizeSpanAttribute),
      };
    }),
  };
}

function sanitizeSpanAttribute(attribute: PageBlockTextSpanAttribute): PageBlockTextSpanAttribute {
  switch (attribute.type) {
    case "Bold":
      return { type: "Bold" };
    case "Code":
      return { type: "Code" };
    case "Strikethrough":
      return { type: "Strikethrough" };
    case "Italic":
      return { type: "Italic" };
    case "Comment":
      const { commentHighlightId, commentIsResolved } = attribute;
      const isCommentIsResolvedDefined = commentIsResolved !== undefined && commentIsResolved !== null;

      return {
        type: "Comment",
        ...(isCommentIsResolvedDefined && { commentIsResolved }),
        ...(commentHighlightId && { commentHighlightId }),
      };
    case "Link":
      const { documentationItemId, link, openInNewTab, openInNewWindow } = attribute;
      const isOpenInNewTabDefined = openInNewTab !== undefined && openInNewTab !== null;
      const isOpenInNewWindowDefined = openInNewWindow !== undefined && openInNewWindow !== null;

      return {
        type: "Link",
        ...(isOpenInNewTabDefined && { openInNewTab }),
        ...(isOpenInNewWindowDefined && { openInNewWindow }),
        ...(documentationItemId && { documentationItemId }),
        ...(link && { link }),
      };
  }
}
